import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import FloodIcon from '@mui/icons-material/Flood';

// Severity Styling Utility, adapted for flood risk
const getSeverityStyling = (severity) => {
    const s = severity?.toLowerCase();
    switch (s) {
        case 'high':    return { chipColor: 'error', label: 'High' };
        default:        return { chipColor: 'default', label: 'N/A' };
    }
};

const ImpactedAreasList = ({ floodData, onAreaSelect, selectedAreaId }) => {
    const theme = useTheme();

    const highRiskAreas = useMemo(() => {
        if (!floodData || !floodData.features) {
            return [];
        }

        const highRisk = [];

        floodData.features.forEach((feature, index) => {
            const riskLevel = feature.properties.risk_level;
            // Add a unique ID to each feature for selection tracking
            const featureWithId = { ...feature, id: `flood-area-${index}` };

            if (riskLevel === 'High') {
                highRisk.push(featureWithId);
            }
        });

        return highRisk;
    }, [floodData]);

    const hasDataToList = highRiskAreas.length > 0;

    const renderAreaList = (areas, title) => {
        if (areas.length === 0) return null;

        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                    {title} ({areas.length})
                </Typography>
                <Paper elevation={0} variant="outlined">
                    <List dense disablePadding>
                        {areas.map((area, index) => {
                            const severityStyle = getSeverityStyling(area.properties.risk_level);
                            const isSelected = selectedAreaId === area.id;

                            return (
                                <ListItemButton
                                    key={area.id}
                                    selected={isSelected}
                                    onClick={() => onAreaSelect(area)}
                                    sx={{ borderBottom: index < areas.length - 1 ? `1px solid ${theme.palette.divider}` : 'none' }}
                                >
                                    <ListItemText primary={`Area #${index + 1}`} />
                                    <Chip
                                        label={severityStyle.label}
                                        color={severityStyle.chipColor}
                                        size="small"
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Paper>
            </Box>
        );
    };

    return (
        <Card>
            <CardHeader
                title="Risk areas"
                avatar={<FloodIcon />}
                titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
                {hasDataToList ? (
                    <>
                        {renderAreaList(highRiskAreas, 'High risk areas')}
                    </>
                ) : (
                    <Typography color="text.secondary">
                        No high risk areas were identified in the latest update.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default ImpactedAreasList;